import { useState, useRef, useEffect, useContext, FC, useMemo, Fragment } from 'react';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import { getConfig, getIconByName } from 'utils/helperMethods';
import _ from 'lodash';
import AttributesPanel from './AttributesPanel';
import { queryClient } from 'services/reactQueryService';
import { PricingSimulatorQueryEnum } from 'utils/queryKeysEnum';
import { UserContext } from 'contexts/ConfigContext';
import { Checkbox } from 'antd';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

type Props = {
    disabled?: boolean;
    availableFilters: any[];
    allCheckedAttribute: any;
    allSelectedValueForAllAttribute: any;
    applyFilterObject: any;
    resetFilterObject: Function;
    showIsEditedRecords: boolean;
    setShowIsEditedRecords: Function;
    scenarioId: number;
    onSubmitHandler: (obj: any) => void;
    showAttributePanelToLeft?: boolean;
    hideShowEditedRecordCheckbox?: boolean;
};

const ScenarioFilter: FC<Props> = ({
    disabled,
    availableFilters,
    allCheckedAttribute,
    allSelectedValueForAllAttribute,
    applyFilterObject,
    resetFilterObject,
    showIsEditedRecords,
    setShowIsEditedRecords,
    scenarioId,
    onSubmitHandler,
    showAttributePanelToLeft = false,
    hideShowEditedRecordCheckbox = false,
}) => {
    const [tempShowIsEditedRecords, setTempShowIsEditedRecords] = useState<boolean>(false);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});
    const [tempAllCheckedAttribute, setTempAllCheckedAttribute] = useState<any>({});
    const [tempAllSelectedValueForAllAttribute, setTempAllSelectedValueForAllAttribute] = useState<any>({});
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [filterSelectionVisible, setFilterSelectionVisible] = useState(false);
    const [attributePanelVisible, setAttributePanelVisible] = useState(false);
    const selectionPanelRef = useRef<any>(null);
    const attributePanelRef = useRef<any>(null);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const scenarioFilterDimensionInfo = dimensionConfigData?.scenarioFilterDimensionInfo ?? {};

    const [searchKey, setSearchKey] = useState('');
    const [offset, setOffset] = useState(0);

    const onOpenFilterClick = () => {
        if (disabled) return;
        setFilterSelectionVisible(true);
        setTempAllCheckedAttribute(_.cloneDeep(allCheckedAttribute));
        setTempApplyFilterObject(_.cloneDeep(applyFilterObject));
        setTempAllSelectedValueForAllAttribute(_.cloneDeep(allSelectedValueForAllAttribute));
        setTempShowIsEditedRecords(showIsEditedRecords);
    };

    const handleSelectedFilter = (filter: any) => {
        queryClient.removeQueries({
            queryKey: [PricingSimulatorQueryEnum.FILTER_ATTRIBUTES],
            exact: false,
        });

        setSelectedFilter(filter);
        setAttributePanelVisible(true);
        setOffset(0);
        setSearchKey('');
    };

    const handleSubmit = () => {
        setFilterSelectionVisible(false);
        setSelectedFilter(null);
        setAttributePanelVisible(false);

        const result: any = {
            allCheckedAttribute: _.cloneDeep(tempAllCheckedAttribute),
            allSelectedValueForAllAttribute: _.cloneDeep(tempAllSelectedValueForAllAttribute),
            applyFilterObject: _.cloneDeep(tempApplyFilterObject),
            showIsEditedRecords: tempShowIsEditedRecords,
        };
        onSubmitHandler(result);
    };

    // both for parent and child component if clicked outside close the panels
    const handleClickOutside = (event: any) => {
        if (
            (selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target) &&
                attributePanelRef.current &&
                !attributePanelRef.current?.contains(event.target)) ||
            (!attributePanelRef.current &&
                selectionPanelRef.current &&
                !selectionPanelRef.current?.contains(event.target))
        ) {
            resetInitialState();
        }
    };

    const resetInitialState = () => {
        setFilterSelectionVisible(false);
        setAttributePanelVisible(false);
        setSelectedFilter(null);
    };

    // clearing filter selection made
    const onResetClick = () => {
        resetInitialState();
        resetFilterObject();
        setShowIsEditedRecords(false);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    //separates list of attribute types depending on dimension
    function separateByDimension(arr: any) {
        const result: any = {};
        arr.forEach((item: any) => {
            const dimension = item.dimension;

            if (result && !(dimension in result)) {
                result[dimension] = {
                    label: scenarioFilterDimensionInfo?.[dimension]?.label,
                    items: [],
                };
            }

            result[dimension]?.items?.push(item);
        });

        return result;
    }

    //use this variable just to display in UI
    const updatedAvailableFilters = useMemo(() => {
        return separateByDimension(availableFilters);
    }, [availableFilters]);

    return (
        <div className="relative">
            {/* MAIN BUTTON */}

            <span
                className={`border  flex justify-between items-center py-1 px-3 cursor-pointer rounded ${
                    disabled
                        ? 'cursor-not-allowed border-borderGrey bg-backgroundGreyAlt6  text-promoColour'
                        : 'cursor-pointer'
                } ${filterSelectionVisible ? ' border-purpleFont text-purpleFont' : ' border-borderGreyAl1  '}  w-20  `}
                onClick={onOpenFilterClick}
            >
                {getIconByName('FilterFilled')}
                <span>Filter</span>
            </span>

            {/* SELECTION PANEL */}
            {filterSelectionVisible && (
                <div
                    className={`absolute top-9 p-3 bg-white z-50 shadow-[0px_9px_28px_8px_rgba(0,0,0,0.05)] rounded-lg left-0 w-[258px] flex flex-col items-start  left-[-178px] `}
                    ref={selectionPanelRef}
                >
                    {/* all attributes */}
                    <>
                        <div className="w-full max-h-[348px] overflow-auto">
                            {!hideShowEditedRecordCheckbox && (
                                <Checkbox
                                    checked={tempShowIsEditedRecords}
                                    onChange={(e: any) => setTempShowIsEditedRecords(e.target.checked)}
                                    className="!ml-0 !py-2 !px-3 w-full"
                                >
                                    <span className="font-xs">Edited Records</span>
                                </Checkbox>
                            )}
                            {Object.keys(updatedAvailableFilters)?.map((dimension: any) => {
                                const label = updatedAvailableFilters?.[dimension]?.label || '';
                                const items = updatedAvailableFilters?.[dimension]?.items || [];
                                return (
                                    <Fragment key={dimension}>
                                        <div className="flex my-3 items-center">
                                            <p className="text-promoColour font-xs mr-2">{label}</p>
                                            <p className="w-full flex-1 border-b border-b-backgroundLightGrey"></p>
                                        </div>
                                        {items.map((filter: any) => {
                                            return (
                                                <div
                                                    key={filter?.value}
                                                    className={` rounded cursor-pointer flex items-center ${
                                                        filter?.value === selectedFilter?.value
                                                            ? 'bg-bgBackground text-purpleFont'
                                                            : ''
                                                    }`}
                                                >
                                                    <div
                                                        className="w-full flex gap-2 justify-between items-center py-2 px-3 rounded"
                                                        onClick={() => handleSelectedFilter(filter)}
                                                    >
                                                        <div className="w-full flex gap-2 font-xs items-center  ">
                                                            <span> {filter?.label}</span>
                                                        </div>
                                                        {filter.value !== selectedFilter?.value && <RightArrowAlt />}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </div>
                    </>

                    {/* BUTTONS */}
                    <div className="flex gap-2 items-center justify-end  mt-2 pt-2 w-full border-t border-backgroundGreyAlt1">
                        <span
                            className="cursor-pointer py-1 px-3 rounded border border-borderGreyAl1"
                            onClick={onResetClick}
                        >
                            Reset
                        </span>
                        <span
                            onClick={handleSubmit}
                            className="bg-purpleFont font-xs cursor-pointer py-1 px-3 text-white rounded"
                        >
                            Apply Filter
                        </span>
                    </div>
                </div>
            )}
            {/* ATTRIBUTES PANEL */}
            {attributePanelVisible && selectedFilter && (
                <AttributesPanel
                    searchKey={searchKey}
                    setSearchKey={setSearchKey}
                    offset={offset}
                    setOffset={setOffset}
                    scenarioId={scenarioId}
                    selectedFilter={selectedFilter}
                    tempApplyFilterObject={tempApplyFilterObject}
                    setTempApplyFilterObject={setTempApplyFilterObject}
                    tempAllCheckedAttribute={tempAllCheckedAttribute}
                    setTempAllCheckedAttribute={setTempAllCheckedAttribute}
                    tempAllSelectedValueForAllAttribute={tempAllSelectedValueForAllAttribute}
                    setTempAllSelectedValueForAllAttribute={setTempAllSelectedValueForAllAttribute}
                    ref={attributePanelRef}
                    showAttributePanelToLeft={showAttributePanelToLeft}
                />
            )}
        </div>
    );
};
export default ScenarioFilter;
